import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search,
  Person,
  SportsTennis,
  TrendingUp,
  Add,
  Message,
  Star,
  FilterList,
  People,
  Business,
} from '@mui/icons-material';
import apiService from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  avatar_url?: string;
  skill_level?: number; // Deprecated, kept for backward compatibility
  elo_rating?: number;
  initial_elo?: number;
  preferred_side: string;
  club_id?: number;
  club_name?: string;
  total_matches: number;
  total_wins: number;
  win_rate: number;
  is_active: boolean;
  created_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`users-tabpanel-${index}`}
      aria-labelledby={`users-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [skillFilter, setSkillFilter] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsersFromMatches();
      // Ensure response.data is always an array
      const data: any = response.data;
      const matches = Array.isArray(data) ? data : (data?.matches || data?.data || []);
      
      // Extract unique users from matches
      const usersMap = new Map();
      if (Array.isArray(matches)) {
        matches.forEach((match: any) => {
          // Extract users from participants array
          if (match.participants && Array.isArray(match.participants)) {
            match.participants.forEach((participant: any) => {
              if (participant.user_id && participant.user_name) {
                // Create user object from participant data
                const user = {
                  id: participant.user_id,
                  name: participant.user_name,
                  email: '', // Not available in participant data
                  avatar_url: participant.avatar_url || undefined,
                  elo_rating: participant.elo_rating,
                  initial_elo: participant.initial_elo,
                  preferred_side: '', // Not available in participant data
                  club_id: undefined, // Not available in participant data
                  club_name: undefined, // Not available in participant data
                  total_matches: 0, // Not available in participant data
                  total_wins: 0, // Not available in participant data
                  win_rate: 0, // Not available in participant data
                  is_active: true,
                  created_at: '', // Not available in participant data
                };
                // Only add if we don't already have this user, or update if we have more complete data (prefer data with elo_rating)
                const existingUser = usersMap.get(user.id);
                if (!existingUser) {
                  usersMap.set(user.id, user);
                } else {
                  // Update if new user has elo_rating and existing doesn't, or merge data
                  if (user.elo_rating != null && existingUser.elo_rating == null) {
                    usersMap.set(user.id, { ...existingUser, elo_rating: user.elo_rating, initial_elo: user.initial_elo });
                  } else if (existingUser.elo_rating == null && user.elo_rating == null && user.initial_elo != null) {
                    // If both don't have elo_rating, prefer the one with initial_elo
                    usersMap.set(user.id, { ...existingUser, initial_elo: user.initial_elo });
                  }
                }
              }
            });
          }
          // Legacy support: also check for player1, player2, etc. (if matches still use old format)
          if (match.player1) {
            usersMap.set(match.player1.id, match.player1);
          }
          if (match.player2) {
            usersMap.set(match.player2.id, match.player2);
          }
          if (match.player3) {
            usersMap.set(match.player3.id, match.player3);
          }
          if (match.player4) {
            usersMap.set(match.player4.id, match.player4);
          }
        });
      }
      
      setUsers(Array.from(usersMap.values()));
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(userItem => {
    const matchesSearch = userItem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         userItem.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (userItem.club_name && userItem.club_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Use elo_rating or initial_elo for skill filter, fallback to skill_level for backward compatibility
    const userSkill = userItem.elo_rating ?? userItem.initial_elo ?? userItem.skill_level ?? 5;
    const matchesSkill = skillFilter === 'all' || userSkill.toString() === skillFilter;
    
    return matchesSearch && matchesSkill && userItem.is_active;
  });

  const topPlayers = [...filteredUsers]
    .sort((a, b) => b.win_rate - a.win_rate)
    .slice(0, 10);

  const mostActivePlayers = [...filteredUsers]
    .sort((a, b) => b.total_matches - a.total_matches)
    .slice(0, 10);

  const recentPlayers = [...filteredUsers]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 10);

  const renderUserCard = (userItem: User) => (
    <Card key={userItem.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            src={userItem.avatar_url}
            sx={{ width: 60, height: 60 }}
          >
            <Person />
          </Avatar>
          <Box flexGrow={1}>
            <Typography variant="h6" gutterBottom>
              {userItem.name}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Chip
                label={`Skill: ${userItem.skill_level || userItem.initial_elo || 5}`}
                color="primary"
          size="small"
        />
        <Chip
                label={`ELO: ${(userItem.elo_rating != null ? userItem.elo_rating : (userItem.initial_elo != null ? userItem.initial_elo : 5.0)).toFixed(2)}`}
                color="primary"
                variant="outlined"
          size="small"
        />
        <Chip
                label={userItem.preferred_side}
                variant="outlined"
          size="small"
        />
              {userItem.club_name && (
        <Chip
                  label={userItem.club_name}
                  color="secondary"
          size="small"
        />
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <SportsTennis fontSize="small" />
                <Typography variant="body2">
                  {userItem.total_matches} matches
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <TrendingUp fontSize="small" />
                <Typography variant="body2">
                  {userItem.win_rate}% win rate
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gap={1}>
            <Tooltip title="Send Message">
              <IconButton size="small">
                <Message />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Profile">
              <IconButton size="small">
                <Person />
              </IconButton>
            </Tooltip>
            <Tooltip title="Challenge to Match">
              <IconButton size="small" color="primary">
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Players
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Connect with other padel players in your community
          </Typography>
      </Box>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center">
          <Box flexGrow={1}>
            <TextField
              fullWidth
              placeholder="Search players by name, email, or club..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box minWidth={200}>
            <FormControl fullWidth>
              <InputLabel>Skill Level</InputLabel>
              <Select
                value={skillFilter}
                label="Skill Level"
                onChange={(e) => setSkillFilter(e.target.value)}
              >
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="1">Level 1</MenuItem>
                <MenuItem value="2">Level 2</MenuItem>
                <MenuItem value="3">Level 3</MenuItem>
                <MenuItem value="4">Level 4</MenuItem>
                <MenuItem value="5">Level 5</MenuItem>
                <MenuItem value="6">Level 6</MenuItem>
                <MenuItem value="7">Level 7</MenuItem>
                <MenuItem value="8">Level 8</MenuItem>
                <MenuItem value="9">Level 9</MenuItem>
                <MenuItem value="10">Level 10</MenuItem>
                </Select>
              </FormControl>
            </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
            >
              More Filters
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3} mb={3}>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <People color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{users.length}</Typography>
                  <Typography color="textSecondary">Total Players</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {Math.round(users.reduce((sum, u) => sum + u.win_rate, 0) / users.length) || 0}%
                  </Typography>
                  <Typography color="textSecondary">Avg Win Rate</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SportsTennis color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {users.reduce((sum, u) => sum + u.total_matches, 0)}
                  </Typography>
                  <Typography color="textSecondary">Total Matches</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Business color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {new Set(users.map(u => u.club_id).filter(Boolean)).size}
                  </Typography>
                  <Typography color="textSecondary">Clubs Represented</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab 
            label={
              <Badge badgeContent={filteredUsers.length} color="primary">
                All Players
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={topPlayers.length} color="secondary">
                Top Players
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={mostActivePlayers.length} color="success">
                Most Active
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={recentPlayers.length} color="info">
                New Players
              </Badge>
            } 
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {filteredUsers.length === 0 ? (
          <Box textAlign="center" py={4}>
            <People sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No players found
            </Typography>
            <Typography color="text.secondary" paragraph>
              Try adjusting your search criteria or check back later for new players.
            </Typography>
          </Box>
        ) : (
          filteredUsers.map(renderUserCard)
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {topPlayers.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Star sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No top players yet
            </Typography>
            <Typography color="text.secondary" paragraph>
              Play more matches to see the leaderboard!
            </Typography>
          </Box>
        ) : (
          topPlayers.map((player, index) => (
            <Card key={player.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: 40 }}>
                    <Typography variant="h6" color="primary">
                      #{index + 1}
                    </Typography>
                  </Box>
                  <Avatar
                    src={player.avatar_url}
                    sx={{ width: 50, height: 50 }}
                  >
                    <Person />
                  </Avatar>
                  <Box flexGrow={1}>
                    <Typography variant="h6" gutterBottom>
                      {player.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={`Skill: ${player.skill_level || player.initial_elo || 5}`}
                        color="primary"
                        size="small"
                      />
                      <Chip
                        label={`ELO: ${(player.elo_rating != null ? player.elo_rating : (player.initial_elo != null ? player.initial_elo : 5.0)).toFixed(2)}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={`${player.win_rate}% win rate`}
                        color="success"
                        size="small"
                      />
                      <Chip
                        label={`${player.total_matches} matches`}
                        size="small"
              />
            </Box>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Challenge">
                      <IconButton size="small" color="primary">
                        <Add />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {mostActivePlayers.length === 0 ? (
          <Box textAlign="center" py={4}>
            <SportsTennis sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No active players yet
            </Typography>
            <Typography color="text.secondary" paragraph>
              Start playing matches to appear here!
            </Typography>
          </Box>
        ) : (
          mostActivePlayers.map(renderUserCard)
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {recentPlayers.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No new players yet
            </Typography>
            <Typography color="text.secondary" paragraph>
              Check back later for new community members!
            </Typography>
          </Box>
        ) : (
          recentPlayers.map(renderUserCard)
        )}
      </TabPanel>
    </Box>
  );
}; 
